import React, { useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { isPersistedSubAgent } from '../../Agents/formUtils';
import { getSubAgentDisplayLabel } from '../constants';
import SubAgentCard from './SubAgentCard';
import SubAgentNavChips from './SubAgentNavChips';

function SubAgentList({
  subAgents,
  onChange,
  onAdd,
  onRemove,
  onRequestImproveInstructions,
  onRequestImproveMcpInstructions,
  isSubmitting,
  isImproveModalSubmitting = false,
  isApplyingImprovePreview = false,
  improvingSubAgentId = null,
  improvingMcpBindingId = null,
  textVersionStack = null,
  knowledgeBases = [],
  defaultKnowledgeBaseId = null,
  llmModelLines = [],
  llmEfforts = [],
  mcpServers = [],
  isEditMode = false,
}) {
  const [improveErrors, setImproveErrors] = useState({});
  const [mcpImproveErrors, setMcpImproveErrors] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [removeCandidateId, setRemoveCandidateId] = useState(null);
  const initializedRef = useRef(false);
  const knownIdsRef = useRef(new Set());
  const cardRefs = useRef({});

  const scrollToCard = (id) => {
    requestAnimationFrame(() => {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const activateSubAgent = (id, { scroll = true } = {}) => {
    setActiveId((current) => (current === id ? null : id));
    if (scroll) {
      scrollToCard(id);
    }
  };

  useEffect(() => {
    const currentIds = new Set(subAgents.map((item) => item.id));

    if (!initializedRef.current && subAgents.length > 0) {
      initializedRef.current = true;
      if (!isEditMode && subAgents.length === 1) {
        setActiveId(subAgents[0].id);
      }
      knownIdsRef.current = currentIds;
      return;
    }

    const newIds = subAgents
      .filter((item) => !knownIdsRef.current.has(item.id))
      .map((item) => item.id);

    if (newIds.length > 0) {
      const newestId = newIds[newIds.length - 1];
      setActiveId(newestId);
      scrollToCard(newestId);
    }

    setActiveId((current) => {
      if (current && !currentIds.has(current)) {
        const remaining = subAgents.map((item) => item.id);
        return remaining[0] || null;
      }
      return current;
    });

    knownIdsRef.current = currentIds;
  }, [subAgents, isEditMode]);

  const handleAdd = () => {
    onAdd();
  };

  const handleRemove = (id) => {
    const remaining = subAgents.filter((item) => item.id !== id);
    if (activeId === id) {
      setActiveId(remaining[0]?.id || null);
    }
    delete cardRefs.current[id];
    onRemove(id);
  };

  const handleRequestRemove = (id) => {
    setRemoveCandidateId(id);
  };

  const handleCancelRemove = () => {
    setRemoveCandidateId(null);
  };

  const handleConfirmRemove = () => {
    if (!removeCandidateId) {
      return;
    }
    handleRemove(removeCandidateId);
    setRemoveCandidateId(null);
  };

  const removeCandidate = removeCandidateId
    ? subAgents.find((item) => item.id === removeCandidateId)
    : null;
  const removeCandidateIndex = removeCandidate
    ? subAgents.findIndex((item) => item.id === removeCandidateId)
    : -1;
  const removeCandidateLabel =
    removeCandidate && removeCandidateIndex >= 0
      ? getSubAgentDisplayLabel(removeCandidate, removeCandidateIndex)
      : '';
  const removeConfirmMessage = removeCandidate
    ? `Tem certeza que deseja remover o comportamento «${removeCandidateLabel}»? As configurações serão perdidas.${
        isEditMode && isPersistedSubAgent(removeCandidate.id)
          ? ' Ao salvar o agente, a exclusão será permanente.'
          : ''
      }`
    : '';

  const handleToggleExpand = (id) => {
    activateSubAgent(id);
  };

  const handleSelectFromChips = (id) => {
    setActiveId(id);
    scrollToCard(id);
  };

  const handleClearImproveError = (id, message) => {
    setImproveErrors((current) => ({ ...current, [id]: message }));
  };

  const handleRequestImproveMcpInstructions = (subAgent, binding) => {
    if (!onRequestImproveMcpInstructions) {
      return;
    }

    const draft = binding.instructions?.trim();
    if (!draft) {
      setMcpImproveErrors((current) => ({
        ...current,
        [binding.id]: 'Escreva um rascunho antes de melhorar o texto.',
      }));
      return;
    }

    if (!binding.mcpServerId) {
      setMcpImproveErrors((current) => ({
        ...current,
        [binding.id]: 'Selecione um servidor MCP antes de melhorar as instruções.',
      }));
      return;
    }

    setMcpImproveErrors((current) => ({ ...current, [binding.id]: '' }));
    onRequestImproveMcpInstructions(subAgent, binding);
  };

  const getMcpImproveError = (bindingId) => mcpImproveErrors[bindingId] || '';
  const pausedCount = subAgents.filter((item) => item.enabled === false).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-content-muted">
          {subAgents.length === 0
            ? 'Nenhum comportamento adicionado.'
            : `${subAgents.length} comportamento${subAgents.length > 1 ? 's' : ''} configurado${subAgents.length > 1 ? 's' : ''}${
                pausedCount > 0 ? ` · ${pausedCount} pausado${pausedCount > 1 ? 's' : ''}` : ''
              }.`}
        </p>
        <button
          type="button"
          onClick={handleAdd}
          className="btn-secondary text-sm px-3 py-2 shrink-0"
        >
          + Adicionar
        </button>
      </div>

      {subAgents.length >= 2 && (
        <SubAgentNavChips
          subAgents={subAgents}
          activeId={activeId}
          onSelect={handleSelectFromChips}
        />
      )}

      {subAgents.length === 0 && (
        <p className="text-sm text-content-muted text-center py-6 border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
          Adicione comportamentos para qualificar contatos, executar habilidades, aplicar regras
          de segurança ou escalar para humano.
        </p>
      )}

      {subAgents.map((subAgent, index) => (
        <div
          key={subAgent.id}
          ref={(node) => {
            if (node) {
              cardRefs.current[subAgent.id] = node;
            } else {
              delete cardRefs.current[subAgent.id];
            }
          }}
        >
          <SubAgentCard
            subAgent={subAgent}
            index={index}
            isActive={activeId === subAgent.id}
            expanded={activeId === subAgent.id}
            onToggleExpand={() => handleToggleExpand(subAgent.id)}
            onRemove={() => handleRequestRemove(subAgent.id)}
            onChange={onChange}
            onRequestImproveInstructions={onRequestImproveInstructions}
            onRequestImproveMcpInstructions={handleRequestImproveMcpInstructions}
            isSubmitting={isSubmitting}
            isImproveModalSubmitting={isImproveModalSubmitting}
            isApplyingImprovePreview={isApplyingImprovePreview}
            improvingSubAgentId={improvingSubAgentId}
            improvingMcpBindingId={improvingMcpBindingId}
            textVersionStack={textVersionStack}
            improveError={improveErrors[subAgent.id]}
            onClearImproveError={handleClearImproveError}
            getMcpImproveError={getMcpImproveError}
            knowledgeBases={knowledgeBases}
            defaultKnowledgeBaseId={defaultKnowledgeBaseId}
            llmModelLines={llmModelLines}
            llmEfforts={llmEfforts}
            mcpServers={mcpServers}
            subAgents={subAgents}
          />
        </div>
      ))}

      <ConfirmModal
        open={Boolean(removeCandidateId)}
        title="Remover comportamento"
        message={removeConfirmMessage}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </div>
  );
}

export default SubAgentList;
